<script lang="ts">
  import { getModalStore } from '@skeletonlabs/skeleton';
  import { clientSsoConfigStore } from '$lib/stores/clientSsoConfigStore';
  import type { ClientSsoConfig, ClientSsoOidcSettings, ClientSsoSamlSettings, UpdateClientSsoConfigRequest } from '$lib/types';
  import { onMount } from 'svelte';

  const modalStore = getModalStore();
  export let parent: any;

  let clientSsoConfig: ClientSsoConfig | null = null;
  let loading = true;
  let error = '';
  let fileInput: HTMLInputElement;

  let isSamlEnabled = false;
  let isOidcEnabled = false;
  // an existing OIDC client secret is kept unless a new one is entered
  let isOidcConfigured = false;
  let samlSettings: ClientSsoSamlSettings = createSamlSettings();
  let oidcSettings: ClientSsoOidcSettings = createOidcSettings();
  let oidcScopes = '';

  onMount(async () => {
    const clientId = $modalStore[0].meta.clientId || 0;
    await fetchConfig(clientId);
    loading = false;
  });

  async function fetchConfig(clientId: number) {
    try {
      clientSsoConfig = await clientSsoConfigStore.getConfig(clientId);
      if (!clientSsoConfig) return;

      isSamlEnabled = !!clientSsoConfig.saml;
      isOidcEnabled = !!clientSsoConfig.oidc;
      isOidcConfigured = !!clientSsoConfig.oidc;
      samlSettings = clientSsoConfig.saml ?? createSamlSettings();
      oidcSettings = clientSsoConfig.oidc ? { ...clientSsoConfig.oidc, providerClientSecret: '' } : createOidcSettings();
      oidcScopes = oidcSettings.scopes.join(' ');
    } catch (err) {
      error = err instanceof Error ? err.message : 'Error fetching SSO configuration';
    }
  }

  function createSamlSettings(): ClientSsoSamlSettings {
    return { ssoProviderId: null, entityId: '', ssoUrl: '', certificate: '', serviceProviderEntityId: '' };
  }

  function createOidcSettings(): ClientSsoOidcSettings {
    return { issuerUrl: '', providerClientId: '', providerClientSecret: '', scopes: ['openid', 'email', 'profile'], idClaimName: 'sub' };
  }

  function validate(config: ClientSsoConfig): string {
    if (!config.isSsoEnabled) return '';
    if (!config.subdomain?.trim()) return 'Subdomain is required when SSO is enabled';
    if (!isSamlEnabled && !isOidcEnabled) return 'At least one SSO method (SAML or OIDC) must be configured when SSO is enabled';

    if (isSamlEnabled) {
      if (!samlSettings.entityId.trim()) return 'SAML Identity Provider Entity ID is required';
      if (!samlSettings.ssoUrl.trim()) return 'SAML Identity Provider SSO URL is required';
      if (!samlSettings.certificate.trim()) return 'SAML Token Signing Certificate is required';
    }

    if (isOidcEnabled) {
      if (!oidcSettings.issuerUrl.trim()) return 'OIDC Issuer URL is required';
      if (!oidcSettings.providerClientId.trim()) return 'OIDC Client ID is required';
      if (!isOidcConfigured && !oidcSettings.providerClientSecret?.trim()) return 'OIDC Client Secret is required';
    }

    return '';
  }

  async function onFormSubmit() {
    if (!clientSsoConfig) return;

    error = validate(clientSsoConfig);
    if (error) return;

    const request: UpdateClientSsoConfigRequest = {
      clientId: clientSsoConfig.clientId,
      subdomain: clientSsoConfig.subdomain,
      isAutoAccountCreationEnabled: clientSsoConfig.isAutoAccountCreationEnabled,
      isSsoEnabled: clientSsoConfig.isSsoEnabled,
      saml: clientSsoConfig.isSsoEnabled && isSamlEnabled ? samlSettings : null,
      oidc:
        clientSsoConfig.isSsoEnabled && isOidcEnabled
          ? { ...oidcSettings, scopes: oidcScopes.split(/[\s,]+/).filter((scope) => scope) }
          : null,
    };

    try {
      await clientSsoConfigStore.updateConfig(request);
      modalStore.close();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Error updating SSO configuration';
    }
  }

  function downloadCertificate() {
    const blob = new Blob([samlSettings.certificate], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${samlSettings.serviceProviderEntityId}.cer`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function uploadCertificate() {
    fileInput.click();
  }

  function handleFileUpload(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        samlSettings.certificate = e.target?.result as string;
      };
      reader.readAsText(file);
    }
  }

  $: if (clientSsoConfig) {
    samlSettings.serviceProviderEntityId = `${clientSsoConfig.subdomain}.${clientSsoConfig.saasApplicationDomain}`;
  }
</script>

<div class="card w-modal flex max-h-[80vh] flex-col shadow-xl">
  <header class="p-4 text-2xl font-bold">Edit SSO Configuration</header>
  <div class="flex-grow overflow-y-auto p-4">
    {#if loading}
      <p>Loading...</p>
    {:else if clientSsoConfig}
      {#if error}
        <div class="card mb-4 bg-error-500 p-4 text-white">
          <p>{error}</p>
        </div>
      {/if}
      <form class="space-y-4 border border-surface-500 p-4 rounded-container-token">
        <label class="label">
          <span>Client ID</span>
          <input class="input" type="text" value={clientSsoConfig.clientId} readonly />
        </label>
        <label class="label">
          <span>Client Name</span>
          <input class="input" type="text" value={clientSsoConfig.clientName} readonly />
        </label>
        <label class="label">
          <span>Subdomain</span>
          <input class="input" type="text" bind:value={clientSsoConfig.subdomain} />
        </label>
        <div class="space-y-4 border border-surface-500 p-4 rounded-container-token">
          <label class="label flex items-center space-x-2">
            <input type="checkbox" bind:checked={clientSsoConfig.isSsoEnabled} />
            <span class="font-bold">Enable SSO</span>
          </label>
          {#if clientSsoConfig.isSsoEnabled}
            <!-- padded and given a transparent border so its checkbox lines up with the SAML and OIDC checkboxes -->
            <label class="label flex items-center space-x-2 border border-transparent px-4">
              <input type="checkbox" bind:checked={clientSsoConfig.isAutoAccountCreationEnabled} />
              <span>Enable Account Auto-Creation</span>
            </label>
            <div class="space-y-4 border border-surface-500 p-4 rounded-container-token">
              <label class="label flex items-center space-x-2">
                <input type="checkbox" bind:checked={isSamlEnabled} />
                <span class="font-bold">SAML</span>
              </label>
              {#if isSamlEnabled}
                <label class="label">
                  <span>Identity Provider Entity ID</span>
                  <input class="input" type="text" bind:value={samlSettings.entityId} />
                </label>
                <label class="label">
                  <span>Identity Provider SSO URL</span>
                  <input class="input" type="text" bind:value={samlSettings.ssoUrl} />
                </label>
                <div class="label">
                  <span>Token Signing Certificate (Base64)</span>
                  <div class="flex space-x-2">
                    <button type="button" class="variant-filled btn" on:click={downloadCertificate}>Download</button>
                    <button type="button" class="variant-filled btn" on:click={uploadCertificate}>Upload</button>
                    <input type="file" accept=".pem,.crt,.cer" style="display: none;" bind:this={fileInput} on:change={handleFileUpload} />
                  </div>
                </div>
                <label class="label">
                  <span>Service Provider Entity ID</span>
                  <input class="input" type="text" value={samlSettings.serviceProviderEntityId} readonly />
                </label>
              {/if}
            </div>
            <div class="space-y-4 border border-surface-500 p-4 rounded-container-token">
              <label class="label flex items-center space-x-2">
                <input type="checkbox" bind:checked={isOidcEnabled} />
                <span class="font-bold">OIDC</span>
              </label>
              {#if isOidcEnabled}
                <label class="label">
                  <span>Issuer URL</span>
                  <input class="input" type="text" bind:value={oidcSettings.issuerUrl} />
                </label>
                <label class="label">
                  <span>Client ID</span>
                  <input class="input" type="text" bind:value={oidcSettings.providerClientId} />
                </label>
                <label class="label">
                  <span>Client Secret</span>
                  <input
                    class="input"
                    type="password"
                    autocomplete="new-password"
                    placeholder={isOidcConfigured ? 'Leave blank to keep the existing secret' : ''}
                    bind:value={oidcSettings.providerClientSecret}
                  />
                </label>
                <label class="label">
                  <span>Scopes (space separated)</span>
                  <input class="input" type="text" bind:value={oidcScopes} />
                </label>
                <label class="label">
                  <span>ID Claim Name</span>
                  <input class="input" type="text" bind:value={oidcSettings.idClaimName} />
                </label>
              {/if}
            </div>
          {/if}
        </div>
      </form>
    {/if}
  </div>
  <footer class="modal-footer {parent.regionFooter} p-4">
    <button class="btn {parent.buttonNeutral}" on:click={parent.onClose}>{parent.buttonTextCancel}</button>
    <button class="btn {parent.buttonPositive}" on:click={onFormSubmit}>Save Changes</button>
  </footer>
</div>

<style lang="postcss">
  .label:not(:has(input[type='checkbox'])) > span {
    text-wrap: nowrap;
    @apply text-primary-400;
  }

  .input {
    @apply border border-secondary-400;
  }
</style>
